import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';

import { ImageService } from '../../global-store';

import {
  CompanyStructureAdvisorPageRequest,
  CompanyStructureAdvisorStatisticsModel,
  CompanyStructureCompanyModel,
  CompanyStructureFeatureChange,
  CompanyStructureFeaturesModel,
  CompanyStructureModel,
  StatisticsPeriod,
} from '../../model';

import RequestCache from '../../utils/RequestCache';

/**
 * FIN-7157: the company structure of a company admin (Einstellungen → Struktur). The backend limits every call
 * to the admin's own company and the companies below it; non-admins get a 403.
 */
@Injectable({
  providedIn: 'root',
})
export class CompanyStructureService {
  public readonly structure: RequestCache<CompanyStructureModel>;

  constructor(
    private http: HttpClient,
    private imageService: ImageService,
  ) {
    this.structure = new RequestCache(
      this.http
        .get<CompanyStructureModel>(`${environment.apiEndpoint}/account/company/structure`)
        .pipe(this.imageService.loadImages('logoUrl')),
      'companyStructure',
    );
  }

  public static readonly DEFAULT_ADVISOR_PAGE: CompanyStructureAdvisorPageRequest = {
    page: 0,
    pageSize: 25,
    sortField: 'name',
    sortDirection: 'asc',
  };

  public getCompany(
    companyId: number,
    request: CompanyStructureAdvisorPageRequest = CompanyStructureService.DEFAULT_ADVISOR_PAGE,
  ): Observable<CompanyStructureCompanyModel> {
    const params = new HttpParams()
      .set('page', request.page)
      .set('page_size', request.pageSize)
      .set('sort_field', request.sortField)
      .set('sort_direction', request.sortDirection);

    return this.http
      .get<CompanyStructureCompanyModel>(`${environment.apiEndpoint}/account/company/structure/${companyId}`, {
        params,
      })
      .pipe(this.imageService.loadImages('advisors.data.profilePictureUrl'));
  }

  public getAdvisorStatistics(
    companyId: number,
    advisorId: string,
    period: StatisticsPeriod,
  ): Observable<CompanyStructureAdvisorStatisticsModel> {
    const params = new HttpParams().set('period', period);

    return this.http
      .get<CompanyStructureAdvisorStatisticsModel>(
        `${environment.apiEndpoint}/account/company/structure/${companyId}/advisor/${advisorId}`,
        { params },
      )
      .pipe(this.imageService.loadImages('profilePictureUrl'));
  }

  /** The feature switches of the whole structure; root company admins only. */
  public getFeatures(): Observable<CompanyStructureFeaturesModel> {
    return this.http.get<CompanyStructureFeaturesModel>(
      `${environment.apiEndpoint}/account/company/structure/features`,
    );
  }

  /** Applies the changes in order and returns the new state. */
  public updateFeatures(changes: readonly CompanyStructureFeatureChange[]): Observable<CompanyStructureFeaturesModel> {
    return this.http.put<CompanyStructureFeaturesModel>(
      `${environment.apiEndpoint}/account/company/structure/features`,
      changes,
    );
  }
}
